import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { formatDate, formatDateTime, formatTime, humaniseRole, relativeDue } from '../lib/format'
import { supabase } from '../lib/supabase'
import type {
  JobRole,
  ManagerDashboard as ManagerDashboardData,
  ManagerTask,
  StaffProfile,
  TaskGroup,
  Workspace,
  WorkspaceVenue,
} from '../types'
import { BrandMark } from './BrandMark'

type Tab = 'overview' | 'staff' | 'checklists' | 'areas' | 'devices' | 'followups'

type Props = {
  workspace: Workspace
  venue: WorkspaceVenue
  onVenueChange: (venue: WorkspaceVenue) => void
  onWorkspaceRefresh: () => Promise<void>
}

const tabLabels: Array<{ id: Tab; label: string }> = [
  { id: 'overview', label: 'Today' },
  { id: 'staff', label: 'Staff' },
  { id: 'checklists', label: 'Checklists' },
  { id: 'areas', label: 'Areas' },
  { id: 'devices', label: 'Devices' },
  { id: 'followups', label: 'Follow-ups' },
]

function StatusPill({ status }: { status: string }) {
  return <span className={`status-pill status-pill--${status.replaceAll('_', '-')}`}>{status.replaceAll('_', ' ')}</span>
}

function TaskCard({ task }: { task: ManagerTask }) {
  const required = task.progress?.required ?? 0
  const completed = task.progress?.completed ?? 0
  const periodProgress = required > 0 && completed > 0 && completed < required

  return (
    <article className={`task-row task-row--${task.status}`}>
      <div className="task-row__status" aria-hidden="true" />
      <div className="task-row__main">
        <div className="task-row__heading">
          <strong>{task.title}</strong>
          <StatusPill status={task.status} />
        </div>
        <div className="task-row__meta">
          <span>{task.area_name ?? 'Venue-wide'}</span>
          <span>{formatTime(task.due_at)}</span>
          <span>{relativeDue(task.due_at)}</span>
        </div>
        {periodProgress && (
          <div className="progress-line">
            <span style={{ width: `${Math.min(100, (completed / required) * 100)}%` }} />
            <small>{completed} of {required} jobs complete</small>
          </div>
        )}
      </div>
    </article>
  )
}

function StaffManager({
  data,
  onRefresh,
}: {
  data: ManagerDashboardData
  onRefresh: () => Promise<void>
}) {
  const [name, setName] = useState('')
  const [jobRoleIds, setJobRoleIds] = useState<string[]>([])
  const [taskGroupIds, setTaskGroupIds] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [generatedPin, setGeneratedPin] = useState<{ name: string; pin: string } | null>(null)

  async function createProfile(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setMessage(null)
    const { data: result, error } = await supabase.rpc('create_compliance_staff_profile', {
      p_venue_id: data.venue.id,
      p_full_name: name,
      p_job_role_ids: jobRoleIds.length ? jobRoleIds : null,
      p_task_group_ids: taskGroupIds.length ? taskGroupIds : null,
    })
    setBusy(false)

    if (error) {
      setMessage(error.message)
      return
    }

    const created = Array.isArray(result) ? result[0] : result
    setGeneratedPin({ name, pin: created.generated_pin })
    setName('')
    setJobRoleIds([])
    setTaskGroupIds([])
    await onRefresh()
  }

  async function resetPin(profile: StaffProfile) {
    const confirmed = window.confirm(`Generate a new PIN for ${profile.full_name}? The old PIN will stop working immediately.`)
    if (!confirmed) return
    const { data: result, error } = await supabase.rpc('reset_compliance_staff_pin', { p_profile_id: profile.id })
    if (error) return setMessage(error.message)
    const reset = Array.isArray(result) ? result[0] : result
    setGeneratedPin({ name: profile.full_name, pin: reset.generated_pin })
    await onRefresh()
  }

  async function unlock(profile: StaffProfile) {
    const { error } = await supabase.rpc('unlock_compliance_staff_profile', { p_profile_id: profile.id })
    if (error) return setMessage(error.message)
    await onRefresh()
  }

  async function toggleActive(profile: StaffProfile) {
    const action = profile.is_active ? 'deactivate' : 'reactivate'
    if (!window.confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} ${profile.full_name}?`)) return
    const { error } = await supabase.rpc('set_compliance_staff_profile_active', {
      p_profile_id: profile.id,
      p_is_active: !profile.is_active,
    })
    if (error) return setMessage(error.message)
    await onRefresh()
  }

  return (
    <div className="split-layout">
      <section className="surface">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Lightweight profile</span>
            <h2>Add staff member</h2>
          </div>
          <span className="soft-badge">4-digit PIN</span>
        </div>
        <form className="form-stack" onSubmit={createProfile}>
          <label>
            Full name
            <input value={name} onChange={(event) => setName(event.target.value)} required maxLength={120} />
          </label>
          <fieldset>
            <legend>Job roles</legend>
            <div className="choice-grid">
              {data.job_roles.filter((role) => role.is_active).map((role: JobRole) => (
                <label className="choice-chip" key={role.id}>
                  <input
                    type="checkbox"
                    checked={jobRoleIds.includes(role.id)}
                    onChange={(event) => setJobRoleIds((current) => event.target.checked ? [...current, role.id] : current.filter((id) => id !== role.id))}
                  />
                  {role.name}
                </label>
              ))}
            </div>
          </fieldset>
          <fieldset>
            <legend>Task groups</legend>
            <div className="choice-grid">
              {data.task_groups.filter((group) => group.is_active).map((group: TaskGroup) => (
                <label className="choice-chip" key={group.id}>
                  <input
                    type="checkbox"
                    checked={taskGroupIds.includes(group.id)}
                    onChange={(event) => setTaskGroupIds((current) => event.target.checked ? [...current, group.id] : current.filter((id) => id !== group.id))}
                  />
                  {group.name}
                </label>
              ))}
            </div>
          </fieldset>
          {message && <div className="message message--error">{message}</div>}
          <button className="button button--primary" disabled={busy}>{busy ? 'Creating…' : 'Create staff profile'}</button>
        </form>
      </section>

      <section className="surface surface--flush">
        <div className="section-heading surface__padded-heading">
          <div>
            <span className="eyebrow">Venue workforce</span>
            <h2>{data.staff.length} profiles</h2>
          </div>
        </div>
        <div className="staff-list">
          {data.staff.map((profile) => {
            const locked = Boolean(profile.locked_until && new Date(profile.locked_until) > new Date())
            return (
              <article className={`staff-row ${!profile.is_active ? 'staff-row--inactive' : ''}`} key={profile.id}>
                <div className="avatar">{profile.full_name.split(' ').map((part) => part[0]).slice(0, 2).join('')}</div>
                <div className="staff-row__details">
                  <div className="staff-row__title">
                    <strong>{profile.full_name}</strong>
                    {locked && <span className="status-pill status-pill--overdue">PIN locked</span>}
                    {!profile.is_active && <span className="status-pill">Inactive</span>}
                  </div>
                  <small>
                    {[...profile.job_roles.map((role) => role.name), ...profile.task_groups.map((group) => group.name)].join(' · ') || 'No roles assigned'}
                  </small>
                </div>
                <div className="row-actions">
                  {locked && <button className="button button--ghost button--small" onClick={() => void unlock(profile)}>Unlock</button>}
                  <button className="button button--ghost button--small" onClick={() => void resetPin(profile)}>Reset PIN</button>
                  <button className="button button--ghost button--small" onClick={() => void toggleActive(profile)}>
                    {profile.is_active ? 'Deactivate' : 'Reactivate'}
                  </button>
                </div>
              </article>
            )
          })}
        </div>
      </section>

      {generatedPin && (
        <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="pin-title">
          <div className="modal-card">
            <span className="eyebrow">Show once</span>
            <h2 id="pin-title">PIN for {generatedPin.name}</h2>
            <div className="pin-display">{generatedPin.pin}</div>
            <p>Give this PIN directly to the staff member. VenTuhi stores a secure hash and cannot reveal it again.</p>
            <button className="button button--primary button--large" onClick={() => setGeneratedPin(null)}>I have recorded the PIN</button>
          </div>
        </div>
      )}
    </div>
  )
}

function DeviceManager({ data, onRefresh }: { data: ManagerDashboardData; onRefresh: () => Promise<void> }) {
  const [name, setName] = useState('')
  const [mode, setMode] = useState<'application' | 'full_device'>('application')
  const [areaIds, setAreaIds] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const [pairing, setPairing] = useState<{ code: string; expires: string } | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function createDevice(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    const { data: result, error: rpcError } = await supabase.rpc('create_compliance_kiosk_device', {
      p_venue_id: data.venue.id,
      p_name: name,
      p_device_mode: mode,
      p_area_ids: areaIds.length ? areaIds : null,
    })
    setBusy(false)
    if (rpcError) return setError(rpcError.message)
    const device = Array.isArray(result) ? result[0] : result
    setPairing({ code: device.pairing_code, expires: device.expires_at })
    setName('')
    setAreaIds([])
    await onRefresh()
  }

  return (
    <div className="split-layout">
      <section className="surface">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Area-routed kiosk</span>
            <h2>Pair a device</h2>
          </div>
        </div>
        <form className="form-stack" onSubmit={createDevice}>
          <label>
            Device name
            <input value={name} onChange={(event) => setName(event.target.value)} placeholder="Barback Room Tablet" required />
          </label>
          <label>
            Device mode
            <select value={mode} onChange={(event) => setMode(event.target.value as 'application' | 'full_device')}>
              <option value="application">Shared computer · lock VenTuhi only</option>
              <option value="full_device">Dedicated tablet · full-device kiosk</option>
            </select>
          </label>
          <fieldset>
            <legend>Responsible areas</legend>
            <div className="choice-grid">
              {data.areas.filter((area) => area.node_type === 'area').map((area) => (
                <label className="choice-chip" key={area.id}>
                  <input
                    type="checkbox"
                    checked={areaIds.includes(area.id)}
                    onChange={(event) => setAreaIds((current) => event.target.checked ? [...current, area.id] : current.filter((id) => id !== area.id))}
                  />
                  {area.name}
                </label>
              ))}
            </div>
          </fieldset>
          {error && <div className="message message--error">{error}</div>}
          <button className="button button--primary" disabled={busy || areaIds.length === 0}>{busy ? 'Creating…' : 'Create pairing code'}</button>
        </form>
      </section>

      <section className="surface surface--flush">
        <div className="section-heading surface__padded-heading">
          <div>
            <span className="eyebrow">Paired and pending</span>
            <h2>{data.kiosks.length} devices</h2>
          </div>
        </div>
        <div className="staff-list">
          {data.kiosks.map((device) => (
            <article className="staff-row" key={device.id}>
              <div className="device-icon" aria-hidden="true">▣</div>
              <div className="staff-row__details">
                <div className="staff-row__title">
                  <strong>{device.name}</strong>
                  <StatusPill status={device.status} />
                </div>
                <small>{device.device_mode === 'full_device' ? 'Full-device tablet' : 'Shared-computer app'} · {device.areas.map((area) => area.name).join(', ') || 'No areas'}</small>
                <small>{device.last_seen_at ? `Last seen ${formatDateTime(device.last_seen_at)}` : 'Not activated yet'}</small>
              </div>
            </article>
          ))}
        </div>
      </section>

      {pairing && (
        <div className="modal-backdrop" role="dialog" aria-modal="true">
          <div className="modal-card">
            <span className="eyebrow">Pairing code</span>
            <h2>Open VenTuhi kiosk on the device</h2>
            <div className="pin-display pin-display--pairing">{pairing.code}</div>
            <p>On the device, open the Compliance portal with <strong>Kiosk mode</strong> and enter this code before {formatTime(pairing.expires)}.</p>
            <button className="button button--primary button--large" onClick={() => setPairing(null)}>Done</button>
          </div>
        </div>
      )}
    </div>
  )
}

export function ManagerDashboard({ workspace, venue, onVenueChange }: Props) {
  const [data, setData] = useState<ManagerDashboardData | null>(null)
  const [tab, setTab] = useState<Tab>('overview')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [generating, setGenerating] = useState(false)

  async function loadDashboard() {
    setLoading(true)
    setError(null)
    const { data: result, error: rpcError } = await supabase.rpc('get_compliance_manager_dashboard', {
      p_venue_id: venue.id,
    })
    if (rpcError) setError(rpcError.message)
    else setData(result as ManagerDashboardData)
    setLoading(false)
  }

  useEffect(() => {
    void loadDashboard()
  }, [venue.id])

  const taskBuckets = useMemo(() => {
    const tasks = data?.tasks ?? []
    const now = Date.now()
    const active = tasks.filter((task) => ['available', 'in_progress'].includes(task.status))
    const dueThreshold = now + 60 * 60 * 1000
    return {
      overdue: active.filter((task) => new Date(task.due_at).getTime() < now),
      due: active.filter((task) => {
        const due = new Date(task.due_at).getTime()
        return due >= now && due <= dueThreshold
      }),
      longerWindow: active.filter((task) => new Date(task.due_at).getTime() > dueThreshold),
      completed: tasks.filter((task) => ['completed', 'completed_late'].includes(task.status)),
      upcoming: tasks.filter((task) => task.status === 'upcoming'),
    }
  }, [data])

  async function generateTasks() {
    if (!data) return
    setGenerating(true)
    const { error: rpcError } = await supabase.rpc('generate_compliance_tasks_for_trading_date', {
      p_venue_id: venue.id,
      p_trading_date: data.trading_date,
    })
    if (rpcError) setError(rpcError.message)
    await supabase.rpc('refresh_compliance_task_statuses', { p_venue_id: venue.id })
    await loadDashboard()
    setGenerating(false)
  }

  async function signOut() {
    await supabase.auth.signOut()
  }

  if (loading && !data) {
    return <main className="loading-screen"><BrandMark /><div className="spinner" /><p>Loading Compliance workspace…</p></main>
  }

  if (!data) {
    return <main className="loading-screen"><BrandMark /><div className="message message--error">{error ?? 'Unable to load Compliance.'}</div><button className="button button--primary" onClick={() => void loadDashboard()}>Try again</button></main>
  }

  return (
    <div className="manager-shell">
      <header className="topbar">
        <BrandMark compact />
        <div className="topbar__venue">
          <label className="sr-only" htmlFor="venue-select">Venue</label>
          <select
            id="venue-select"
            value={venue.id}
            onChange={(event) => {
              const next = workspace.venues.find((item) => item.id === event.target.value)
              if (next) onVenueChange(next)
            }}
          >
            {workspace.venues.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
          </select>
          <span>{venue.local_display_label ?? humaniseRole(venue.role)}</span>
        </div>
        <div className="topbar__actions">
          <a className="button button--ghost button--small" href="/?mode=kiosk">Kiosk mode</a>
          <button className="button button--ghost button--small" onClick={() => void signOut()}>Sign out</button>
        </div>
      </header>

      <nav className="tabbar" aria-label="Compliance sections">
        {tabLabels.map((item) => (
          <button key={item.id} className={tab === item.id ? 'active' : ''} onClick={() => setTab(item.id)}>{item.label}</button>
        ))}
      </nav>

      <main className="manager-main">
        <section className="page-heading">
          <div>
            <span className="eyebrow">Trading date · {formatDate(`${data.trading_date}T12:00:00`)}</span>
            <h1>{tab === 'overview' ? 'Compliance control room' : tabLabels.find((item) => item.id === tab)?.label}</h1>
            <p>
              {tab === 'overview' && `${data.venue.name} · cutoff ${data.venue.trading_day_cutoff.slice(0, 5)} · local time ${new Date(data.local_now).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`}
              {tab === 'staff' && 'Onboard routine staff, reset forgotten PINs and keep the audit trail tidy.'}
              {tab === 'checklists' && 'One reporting identity, flexible schedule rules and protected central standards.'}
              {tab === 'areas' && 'Operational areas keep tasks, failures and kiosk alerts exactly where they belong.'}
              {tab === 'devices' && 'Pair shared computers or full-device tablets to the areas they serve.'}
              {tab === 'followups' && 'Track failed checks separately from honest checklist completion.'}
            </p>
          </div>
          <div className="page-heading__actions">
            <button className="button button--ghost" onClick={() => void loadDashboard()}>Refresh</button>
            {tab === 'overview' && <button className="button button--primary" disabled={generating} onClick={() => void generateTasks()}>{generating ? 'Checking schedules…' : 'Generate today’s tasks'}</button>}
          </div>
        </section>

        {error && <div className="message message--error">{error}</div>}

        {tab === 'overview' && (
          <>
            <section className="metric-grid">
              <article className={`metric-card ${data.summary.overdue > 0 ? 'metric-card--alert' : ''}`}><span>Overdue</span><strong>{data.summary.overdue}</strong><small>After grace period</small></article>
              <article className="metric-card"><span>Due now</span><strong>{taskBuckets.due.length}</strong><small>Due within the next hour</small></article>
              <article className="metric-card metric-card--mint"><span>Completed</span><strong>{data.summary.completed_today}</strong><small>Current trading date</small></article>
              <article className="metric-card"><span>Open actions</span><strong>{data.summary.open_follow_ups}</strong><small>Corrective follow-ups</small></article>
            </section>

            <section className="dashboard-grid">
              <div className="surface surface--flush dashboard-grid__tasks">
                <div className="section-heading surface__padded-heading">
                  <div><span className="eyebrow">Live task board</span><h2>What needs attention</h2></div>
                  <span className="soft-badge">{data.tasks.length} generated</span>
                </div>
                <div className="task-list">
                  {taskBuckets.overdue.map((task) => <TaskCard key={task.id} task={task} />)}
                  {taskBuckets.due.map((task) => <TaskCard key={task.id} task={task} />)}
                  {taskBuckets.upcoming.slice(0, 6).map((task) => <TaskCard key={task.id} task={task} />)}
                  {taskBuckets.longerWindow.slice(0, 6).map((task) => <TaskCard key={task.id} task={task} />)}
                  {taskBuckets.overdue.length + taskBuckets.due.length + taskBuckets.upcoming.length + taskBuckets.longerWindow.length === 0 && <div className="empty-state">No live tasks for this window. The weekly lists are quietly minding their own deadline.</div>}
                </div>
              </div>

              <aside className="dashboard-grid__side">
                <section className="surface">
                  <div className="section-heading"><div><span className="eyebrow">This venue</span><h2>Operational pulse</h2></div></div>
                  <dl className="key-values">
                    <div><dt>Areas</dt><dd>{data.areas.filter((area) => area.node_type === 'area').length}</dd></div>
                    <div><dt>Active staff</dt><dd>{data.summary.active_staff}</dd></div>
                    <div><dt>Kiosks</dt><dd>{data.summary.active_kiosks}</dd></div>
                    <div><dt>Templates</dt><dd>{data.templates.length}</dd></div>
                  </dl>
                </section>
                <section className="surface surface--accent">
                  <span className="eyebrow">Alert defaults</span>
                  <h2>Calm first, loud later.</h2>
                  <p>Tasks get {data.venue.overdue_grace_minutes} minutes of grace before an audible kiosk alert, then repeat every {data.venue.alert_repeat_minutes} minutes.</p>
                </section>
              </aside>
            </section>
          </>
        )}

        {tab === 'staff' && <StaffManager data={data} onRefresh={loadDashboard} />}

        {tab === 'checklists' && (
          <section className="card-grid">
            {data.templates.map((template) => (
              <article className="surface checklist-card" key={template.id}>
                <div className="checklist-card__top">
                  <span className="category-badge">{template.category}</span>
                  <StatusPill status={template.status} />
                </div>
                <h2>{template.name}</h2>
                <p>{template.owner_scope === 'venue' ? 'Venue-owned checklist' : `${humaniseRole(template.owner_scope)} standard`} · {template.scoring_mode} reporting</p>
                <div className="schedule-list">
                  {template.schedule_rules.map((rule) => (
                    <div key={rule.id}>
                      <strong>{rule.name}</strong>
                      <span>{humaniseRole(rule.schedule_kind)}</span>
                    </div>
                  ))}
                </div>
                <footer>
                  <span>{template.show_in_kiosk ? 'Shown on kiosks' : 'Account-only'}</span>
                  <span>{template.local_tasks_allowed ? 'Local tasks allowed' : 'Core locked'}</span>
                </footer>
              </article>
            ))}
          </section>
        )}

        {tab === 'areas' && (
          <section className="surface">
            <div className="section-heading"><div><span className="eyebrow">Venue map</span><h2>{data.venue.name}</h2></div><span className="soft-badge">General Manager controlled</span></div>
            <div className="area-grid">
              {data.areas.map((area) => (
                <article className="area-card" key={area.id}>
                  <span className="area-card__icon" aria-hidden="true">{area.node_type === 'zone' ? '◇' : '□'}</span>
                  <div><strong>{area.name}</strong><small>{humaniseRole(area.node_type)}</small></div>
                </article>
              ))}
            </div>
          </section>
        )}

        {tab === 'devices' && <DeviceManager data={data} onRefresh={loadDashboard} />}

        {tab === 'followups' && (
          <section className="surface surface--flush">
            <div className="section-heading surface__padded-heading"><div><span className="eyebrow">Corrective actions</span><h2>{data.follow_ups.length} open</h2></div></div>
            <div className="staff-list">
              {data.follow_ups.map((followUp) => (
                <article className="staff-row" key={followUp.id}>
                  <div className="device-icon" aria-hidden="true">!</div>
                  <div className="staff-row__details">
                    <div className="staff-row__title"><strong>{followUp.title}</strong><StatusPill status={followUp.status} /></div>
                    <small>{followUp.area_name ?? 'Venue-wide'}{followUp.due_at ? ` · Due ${formatDateTime(followUp.due_at)}` : ''}</small>
                    {followUp.description && <p>{followUp.description}</p>}
                  </div>
                </article>
              ))}
              {data.follow_ups.length === 0 && <div className="empty-state">No open follow-up actions.</div>}
            </div>
          </section>
        )}
      </main>
    </div>
  )
}
